import { useState, useRef } from 'react';
import { contactData } from '@/mocks/homeData';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9mr84sit35ii3jdll30';

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setStatus('success');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const formData = new FormData(form);
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && (parsed as { code?: string })?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed as { meta?: { message?: string } })?.meta?.message
          || (parsed as { message?: string })?.message
          || responseText;
        if (typeof serverMsg === 'string' && serverMsg.includes('spam')) {
          setStatus('success');
          return;
        }
        setStatus('error');
        setErrorMsg(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络错误，请稍后重试');
    } finally {
      setTimeout(() => {
        if (status !== 'error') setStatus('idle');
      }, 4000);
    }
  };

  return (
    <section id="contact" className="py-14 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
              {contactData.title}
            </h2>
            <p className="text-sm text-foreground-600 max-w-2xl mx-auto">
              {contactData.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-background-100 rounded-lg p-8 border border-background-200/70">
              <h3 className="font-heading text-lg text-foreground-950 mb-6">发送消息</h3>
              <form
                ref={formRef}
                className="space-y-4"
                data-readdy-form=""
                onSubmit={handleSubmit}
              >
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                    你的名字
                  </label>
                  <input
                    type="text"
                    name="name"
                    placeholder="请输入你的名字"
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                    电子邮箱
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="请输入你的邮箱地址"
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    name="message"
                    rows={3}
                    maxLength={500}
                    placeholder="请输入你想说的话..."
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  ></textarea>
                </div>
                <div className="honeypot-field">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>

                {status === 'success' && (
                  <p className="text-sm text-green-600 font-medium">消息已发送成功，我们会尽快回复你！</p>
                )}
                {status === 'error' && (
                  <p className="text-sm text-red-500 font-medium">{errorMsg}</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                >
                  {status === 'loading' ? '发送中...' : '发送消息'}
                  <i className="ri-send-plane-line ml-2"></i>
                </button>
              </form>
            </div>

            <div className="flex flex-col justify-center gap-6">
              <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">电子邮箱</p>
                    <p className="text-sm text-foreground-600">{contactData.email}</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg">
                    <i className="ri-share-line text-lg"></i>
                  </span>
                  <p className="text-sm font-medium text-foreground-950">关注我们</p>
                </div>
                <div className="flex gap-3">
                  {contactData.socialLinks.map((link) => (
                    <a
                      key={link.name}
                      href="#"
                      className="flex items-center gap-2 px-4 py-2 bg-background-50 border border-background-300 rounded-md text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <i className={`${link.icon} text-base`}></i>
                      {link.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}